// @flow
import React, { useLayoutEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import Dialog from '@material-ui/core/Dialog';

import SaveTranscriptDialog1 from './SaveTranscriptDialog1';
import SaveTranscriptDialog2 from './SaveTranscriptDialog2';

import { updateUserFeaturesRequestAsync } from '../../actions/user';
import { selectTranscripts } from '../../selectors/scribe-conversation';
import { selectUser } from '../../selectors/user';

import { getSocketIfExist } from '../../utils/getSocket';
import { sendSaveTranscriptUpdateMessage } from '../../utils/ws-v1';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
  },
  scrollPaper: {
    background: theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.1)',
  },
  dialog1Paper: {
    justifyContent: 'space-around',
    padding: '0 48px',
    background: theme.palette.type === 'light' ? theme.palette.ava.white : theme.palette.ava.darkBg2,
    borderRadius: 20,
    height: '64%',
    maxHeight: 565,
    [theme.breakpoints.down('sm')]: {
      height: '94%',
    },
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      bottom: 0,
      margin: 0,
      padding: '0 24px',
      height: '100%',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  dialog2Paper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    padding: '10px 20px',
    minHeight: 250,
    maxWidth: 464,
    background: theme.palette.type === 'light' ? theme.palette.ava.white : theme.palette.ava.dark2,
    borderRadius: 20,
    [theme.breakpoints.down('xs')]: {
      margin: 10,
      padding: 10,
    },
  },
}));

interface Props {
  callback: Function;
  firebaseUser: Object;
}

const SaveTranscriptsPopup = ({ callback, firebaseUser }: Props) => {
  const classes = useStyles();
  const user = useSelector(selectUser);
  const transcripts = useSelector(selectTranscripts);
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(true);
  const [dialogNumber, setDialogNumber] = useState(1);

  const isSaveTranscript = !!user?.features?.saveTranscript;
  const isForbidConversationSaving = !!user?.features?.['forbid-conversation-saving'];

  useLayoutEffect(() => {
    if (firebaseUser?.isAnonymous) {
      setIsOpen(false);
      callback({ saveClicked: false });
      return;
    }

    const shouldCallCallback = isSaveTranscript || isForbidConversationSaving;

    if (shouldCallCallback || !Object.keys(transcripts).length) {
      setIsOpen(false);
      callback({ saveClicked: false });
    }
  }, []);

  const onSaveClicked = async () => {
    await dispatch(
      updateUserFeaturesRequestAsync({
        avaId: user.avaId,
        features: { saveTranscript: true },
      })
    );
    const socket = getSocketIfExist();
    if (socket) {
      sendSaveTranscriptUpdateMessage(socket, true);
    }
    await callback({ saveClicked: true });
  };

  const onNotNowClicked = async () => {
    if (dialogNumber === 2) {
      await callback({ saveClicked: false });
    } else {
      setDialogNumber(2);
    }
  };

  return (
    <Dialog
      className={classes.root}
      classes={{
        scrollPaper: classes.scrollPaper,
        paper: dialogNumber === 1 ? classes.dialog1Paper : classes.dialog2Paper,
      }}
      open={isOpen}
      onClose={() => true}
    >
      {dialogNumber === 1 ? (
        <SaveTranscriptDialog1 onSaveClicked={onSaveClicked} onNotNowClicked={onNotNowClicked} />
      ) : (
        <SaveTranscriptDialog2 onSaveClicked={onSaveClicked} onContinueClicked={onNotNowClicked} />
      )}
    </Dialog>
  );
};

const mapStateToProps = ({ firebase: { user } }) => ({
  firebaseUser: user,
});

export default connect(mapStateToProps)(SaveTranscriptsPopup);
